import {handleActions} from 'redux-actions';
import {Record, Map, OrderedSet} from 'immutable';
import moment from 'moment-timezone';

import * as LeaveRegistrationActions from 'web-app/react/profile/child-profile/leave-registration/actions';
import FoodConsumptionActionTypes from 'web-app/react/entities/meals/food-consumption/action-types';
import * as SleepModalActions from 'web-app/react/modals-container/sleep/actions';
import * as DiaperModalActions from 'web-app/react/modals-container/diaper/actions';
import * as ApplyCreamModalActions from 'web-app/react/modals-container/apply-cream/actions';
import * as RegisterTemperatureModalActions from 'web-app/react/modals-container/register-temperature/actions';
import * as NewEmployeeActions from 'web-app/react/profile/new-employee/actions';
import * as StatusActions from 'web-app/react/entities/statuses/actions';
import * as PeoplePickerActions from 'web-app/react/people-picker/actions';
import * as GroupActionsMenuActions from 'web-app/react/institution-people/group-actions-menu/actions';
import * as LeaveRegistrationConstants from 'web-app/react/profile/child-profile/leave-registration/constants';

import * as Actions from './actions';

const PeopleState = Record({
    allChildrenIds: OrderedSet(),
    allEmployeesIds: OrderedSet(),
    selectedChildrenIds: OrderedSet(),
    selectedEmployeesIds: OrderedSet(),
    fetching: false,
    didFetch: false,
    statusRegistrationStats: Map(),
    allergiesAndDietary: {},
});

const togglePerson = (selectedPersonIds, newPersonId) => {
    if (selectedPersonIds.includes(newPersonId)) {
        return selectedPersonIds.delete(newPersonId);
    }
    return selectedPersonIds.add(newPersonId);
};

const emptySelection = state =>
    state.merge({
        selectedEmployeesIds: OrderedSet(),
        selectedChildrenIds: OrderedSet(),
    });

export default handleActions(
    {
        [Actions.toggleChild.type]: (state, {payload}) => {
            return state.merge({
                selectedChildrenIds: togglePerson(state.selectedChildrenIds, payload.childId),
            });
        },
        [Actions.selectChildren.type]: (state, {payload}) =>
            state.merge({
                selectedChildrenIds: OrderedSet(payload.childrenIds),
            }),
        [Actions.toggleEmployee.type]: (state, {payload}) => {
            return state.merge({
                selectedEmployeesIds: togglePerson(state.selectedEmployeesIds, payload.employeeId),
            });
        },
        [Actions.selectEmployees.type]: (state, {payload}) =>
            state.merge({
                selectedEmployeesIds: OrderedSet(payload.employeesIds),
            }),
        [Actions.fetchAll.type]: state =>
            state.merge({
                fetching: true,
            }),
        [Actions.resetFetching.type]: state =>
            state.merge({
                didFetch: false,
            }),
        [Actions.fetchAllSuccess.type]: (state, {payload}) =>
            state.merge({
                fetching: false,
                didFetch: true,
                allChildrenIds: payload.childrenIds,
                allEmployeesIds: payload.employeesIds,
            }),
        [Actions.fetchAllFailed.type]: state =>
            state.merge({
                fetching: false,
            }),
        [Actions.setStatusRegistrations.type]: (state, {payload}) => {
            return state.merge({
                statusRegistrationStats: Map(payload.statusRegistrationStats),
            });
        },
        [Actions.emptySelection.type]: state =>
            state.merge({
                selectedEmployeesIds: OrderedSet(),
                selectedChildrenIds: OrderedSet(),
            }),
        /**
         * All these are ported from children-and-staff ember router
         */
        [FoodConsumptionActionTypes.MEAL_REGISTRATIONS_CREATE_SUCCESS]: state => emptySelection(state),
        [SleepModalActions.napFailure.type]: state => emptySelection(state),
        [SleepModalActions.napSuccess.type]: state => emptySelection(state),
        [SleepModalActions.checkOnFailure.type]: state => emptySelection(state),
        [SleepModalActions.checkOnSuccess.type]: state => emptySelection(state),
        [DiaperModalActions.diaperFailure.type]: state => emptySelection(state),
        [DiaperModalActions.diaperSuccess.type]: state => emptySelection(state),
        [ApplyCreamModalActions.applyCreamSuccess.type]: state => emptySelection(state),
        [RegisterTemperatureModalActions.registerTemperatureSuccess.type]: state => emptySelection(state),
        [GroupActionsMenuActions.washHandsSuccess.type]: state => emptySelection(state),
        [StatusActions.applyStatusFailed.type]: state => emptySelection(state),
        [StatusActions.endStatusFailed.type]: state => emptySelection(state),
        [NewEmployeeActions.createNewEmployeeSuccess.type]: state => emptySelection(state),
        [PeoplePickerActions.submitSuccess.type]: state => emptySelection(state),
        [StatusActions.applyStatusSuccess.type]: state => emptySelection(state),
        [StatusActions.endStatusSuccess.type]: state => emptySelection(state),
        [LeaveRegistrationActions.updateSuccess.type]: (state, action) => {
            const alteredState = closeIfDateInRange(state, action.payload);
            switch (action.payload.personType) {
                case LeaveRegistrationConstants.CHILD:
                    return alteredState.merge({
                        selectedChildrenIds: OrderedSet(),
                    });
                case LeaveRegistrationConstants.EMPLOYEE:
                    return alteredState.merge({
                        selectedEmployeesIds: OrderedSet(),
                    });
                default:
                    return alteredState;
            }
        },
    },
    new PeopleState(),
);

const closeIfDateInRange = (state, {startDate, endDate}) => {
    const isTodayInRange =
        moment(startDate).isSameOrBefore(moment(), 'days') && moment(endDate).isSameOrAfter(moment(), 'days');
    if (!isTodayInRange) {
        return emptySelection(state);
    }
    return state;
};
